export type LegacyReviewAccess = "imported" | "restricted" | "none";
export const REVIEW_MEDIAN_OPTIONS = ["A", "A-", "B+", "B", "B-", "C+", "Unsure"] as const;
export type ReviewMedian = typeof REVIEW_MEDIAN_OPTIONS[number];

export interface ProfessorSummary {
  name: string;
  reviewCount: number;
  slug?: string;
}

export interface OfficialOffering {
  term: string;
  periods: string[];
  instructors: string[];
  sectionCount: number;
}

export interface LegacyReview {
  id: string;
  professor: string;
  term: string;
  median?: ReviewMedian | null;
  comments: string;
  source: "legacy";
  createdAt?: string;
}

export interface CourseSummary {
  courseId: string;
  courseKey: string;
  legacyCourseId: string | null;
  slug: string;
  shortName: string;
  title: string;
  department: string;
  departmentName: string;
  orcUrl: string | null;
  description: string | null;
  distribs: string[];
  offeredLabel: string | null;
  offeredPeriods: string[];
  legacyReviewCount: number;
  typicalMedian?: string | null;
  typicalMedianReviewCount?: number;
  professorCount: number;
  professorNames: string[];
  officialOfferings: OfficialOffering[];
}

export type CourseIndexOverrides = Record<string, CourseSummary>;

export interface CourseSearchRecord {
  courseId: string;
  slug: string;
  shortName: string;
  title: string;
  department: string;
  departmentName: string;
  distribs: string[];
  offeringPreview: string | null;
  legacyReviewCount: number;
  professorNames: string[];
}

export interface MedianCourseRecord extends CourseSearchRecord {
  typicalMedian: string | null;
  typicalMedianReviewCount: number;
}

export interface HomeSummary {
  generatedAt: string;
  departments: DepartmentRecord[];
  spotlight: CourseSearchRecord[];
}

export interface MedianBoardData {
  generatedAt: string;
  currentQuarter: string | null;
  coursesWithMedian: number;
  coursesWithoutMedian: number;
  rankedCourses: MedianCourseRecord[];
}

export interface CourseDetail extends CourseSummary {
  professors: ProfessorSummary[];
  legacyReviewAccess: LegacyReviewAccess;
  legacyReviews: LegacyReview[];
  officialCatalog: {
    inOrc: boolean;
    level: "undergraduate" | "graduate" | "unknown";
  };
  generatedAt: string;
}

export interface DepartmentRecord {
  code: string;
  name: string;
  courseCount: number;
}

export interface ProfessorIndexRecord {
  slug: string;
  name: string;
  reviewCount: number;
  courseCount: number;
  departments: string[];
  mostRecentTerm: string | null;
}

export type ProfessorIndexOverrides = Record<string, ProfessorIndexRecord>;

export interface ProfessorCourseReviews {
  courseId: string;
  courseKey: string;
  slug: string;
  shortName: string;
  title: string;
  department: string;
  departmentName: string;
  reviewCount: number;
  mostRecentTerm: string | null;
  reviews: LegacyReview[];
}

export interface ProfessorDetail extends ProfessorIndexRecord {
  courses: ProfessorCourseReviews[];
  generatedAt: string;
}

export interface SiteSummary {
  courseCount: number;
  departmentCount: number;
  legacyReviewCount: number;
  importedLegacyReviewCount: number;
  generatedAt: string;
}

export interface CourseFileIndexEntry {
  courseId: string;
  slug: string;
}

export interface ProfessorLookupEntry {
  slug: string;
  name: string;
  normalizedName: string;
}

export interface SessionUser {
  id: string;
  email: string;
  isVerified: boolean;
  isAdmin: boolean;
  hasSubmittedReview: boolean;
}

export interface SessionResponse {
  user: SessionUser | null;
}

export interface NewReview {
  id: string;
  courseId: string;
  professor: string;
  term: string;
  median?: ReviewMedian | null;
  comments: string;
  createdAt: string;
  publishedAt: string | null;
  visibility: "visible" | "hidden";
  authorEmail: string;
}

export interface ReviewsResponse {
  reviews: NewReview[];
  canModerate: boolean;
}

export interface ReviewSubmissionResponse {
  ok: true;
  message: string;
  publishAt: string;
}
